import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  BarChart3,
  Building2,
  CalendarClock,
  CalendarDays,
  Download,
  Factory,
  Globe,
  Linkedin,
  Loader2,
  MapPin,
  Printer,
  RefreshCw,
  Sparkles,
  TrendingUp,
  User,
  Users,
} from 'lucide-react';
import { apiError, downloadReportPdf, reportsAPI } from '../services/api';
import { usePoll } from '../lib/usePoll';
import { Alert, Button, Card, ProgressBar, ScoreRing, Spinner, cx } from '../components/ui';
import AskAnything from '../components/AskAnything';
import {
  ContractBlock,
  CoverageWarning,
  HiringBlock,
  InsightList,
  NewsList,
  PatentBlock,
  PeopleBlock,
  ProgramBlock,
  QuoteCard,
  RegulatoryBlock,
  ReportSection,
  ResultsBlock,
  SourcesList,
  SubSection,
  TalkingPointList,
} from '../components/ReportSections';

// The report reads as one continuous document, the same order as the exported
// PDF. It used to be tabbed, which meant opening a report showed only the first
// of four panels - roughly a quarter of what the PDF held - and Print emitted
// just the active tab. These are jump links now, not tabs: everything is on the
// page and this is only a fast way down it.
const CHAPTERS = [
  {
    id: 'ch-brief',
    label: 'What you need to know',
    blurb: 'Read this in the five minutes before your call.',
  },
  {
    id: 'ch-research',
    label: 'The full picture',
    blurb: 'How this company works, where it is heading, and what stands in the way.',
  },
  { id: 'ch-value', label: 'Your pitch', blurb: 'The argument to make in the room.' },
  { id: 'ch-sources', label: 'Where this came from', blurb: 'Every source we used above.' },
] as const;

// Revenue is reported in whatever the company files in - a London bank in
// pounds, a Bengaluru one in rupees - so an amount is only readable next to the
// currency it was stated in. Market cap, which arrives from Finnhub in dollars,
// keeps the dollar default.
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  KRW: '₩',
};

function money(value?: number, currency?: string) {
  if (!value) return null;

  const symbol = currency ? CURRENCY_SYMBOLS[currency] ?? `${currency} ` : '$';

  if (value >= 1e12) return `${symbol}${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `${symbol}${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${symbol}${(value / 1e6).toFixed(1)}M`;
  return `${symbol}${value.toLocaleString()}`;
}

// Quick links are stored as plain label/url pairs, so the mark that belongs
// beside one has to be inferred from what it points at
function linkIcon(link: { label?: string; url?: string }) {
  const text = `${link.label || ''} ${link.url || ''}`.toLowerCase();
  if (text.includes('linkedin')) return Linkedin;
  if (text.includes('crunchbase')) return Building2;
  if (text.includes('finance.yahoo') || text.includes('yahoo finance')) return BarChart3;
  return Globe;
}

// "STAN on Yahoo Finance" is how the link is stored; the cover shows the ticker
function linkLabel(link: { label?: string }) {
  return String(link.label || '').replace(/\s+on\s+Yahoo Finance$/i, '');
}

function day(value: string | Date) {
  return new Date(value).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// A report can be regenerated more than once in a day, so the time is what
// makes "last refreshed" mean anything
function clock(value: string | Date) {
  return new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  // Covers the request that asks for a rewrite, and nothing after it. A rewrite
  // now edits this report rather than filing a new one, so once the server has
  // accepted it the progress arrives on the same document this page is already
  // loading - there is no second report to follow.
  const [restarting, setRestarting] = useState(false);
  const [activeChapter, setActiveChapter] = useState<string>(CHAPTERS[0].id);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [descriptionClamped, setDescriptionClamped] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!id) return;
      try {
        if (!silent) setLoading(true);
        const res = await reportsAPI.getReport(id);
        setReport(res.data);
        setError('');
      } catch (err) {
        // A failed background poll is not worth a banner when the report is
        // already on screen - the next attempt will almost certainly land, and
        // an error over a perfectly readable report is just noise.
        if (!silent) setError(apiError(err, 'Could not load this report'));
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  // A rewrite of a finished report, still running. The report keeps its content
  // and its 'complete' status throughout, so this is the only thing that says
  // the pipeline is working.
  const refreshing = report?.refresh?.status === 'pending';

  // Landing here straight after "Add account" means the report is still being
  // written, so keep checking until the pipeline finishes. Same for a rewrite,
  // which lands as new content under the reader rather than as a new page.
  usePoll(() => load(true), { active: report?.status === 'pending' || refreshing });

  // Keeps the jump bar showing where you are on what is now a long page.
  useEffect(() => {
    if (report?.status !== 'complete') return;

    const targets = CHAPTERS.map((c) => document.getElementById(c.id)).filter(
      (el): el is HTMLElement => Boolean(el)
    );
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (top) setActiveChapter(top.target.id);
      },
      // Top inset matches the two sticky bars (ask island + this nav), so a
      // chapter counts as "current" only once it clears them
      { rootMargin: '-128px 0px -70% 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [report?.status]);

  // "Show more" only earns its place when the text is genuinely cut off, and
  // whether it is depends on the column width - a short profile fits inside the
  // clamp on a wide screen and overflows it on a narrow one. So it is measured
  // rather than guessed at from a character count.
  useEffect(() => {
    if (descriptionOpen) return;

    const measure = () => {
      const el = descriptionRef.current;
      if (el) setDescriptionClamped(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [report?.fastFacts?.description, report?.status, descriptionOpen]);

  const sources = useMemo(() => report?.sources || [], [report]);

  // The homepage belongs in Quick Links, and buildQuickLinks puts it there -
  // but only for accounts that had a website stored when the report ran. Any
  // report written before that carries the address in fastFacts and nowhere
  // else, so it is merged in here rather than left off the cover.
  const quickLinks = useMemo(() => {
    const links = [...(report?.quickLinks || [])];
    const website = report?.fastFacts?.website;
    if (!website) return links;

    const host = (url?: string) =>
      String(url || '')
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .toLowerCase();

    if (!links.some((link: any) => host(link.url) === host(website))) {
      links.unshift({ label: host(website), url: website });
    }

    return links;
  }, [report]);

  /**
   * Rewrite this report with the latest research.
   *
   * There is one report per account per team, so this edits this report rather
   * than filing a second one beside it — that is what used to leave three
   * near-identical reports on one prospect in the list.
   *
   * The page deliberately does not empty itself while it runs: a rep who
   * pressed the button on the way into a call still needs what is on screen.
   * Everything below stays exactly as it was, and is replaced in place only
   * once the new pass has finished. If it fails, what is on screen is still the
   * report.
   */
  const refreshReport = async () => {
    if (!report?.companyId) return;
    setError('');
    setRestarting(true);

    try {
      const res = await reportsAPI.generate(report.companyId);

      // Defensive: this account is the one this report is on, so the server
      // returns this same report. A different id could only mean the account
      // was re-pointed underneath us, and following it beats stranding the user
      // on a page nothing is updating.
      if (res.data?.reportId && res.data.reportId !== id) {
        navigate(`/reports/${res.data.reportId}`);
        return;
      }

      // The refresh marker is written before the response is sent, so this pass
      // is what puts the strip on screen - no separate "starting" state needed.
      await load(true);

      // The shell scrolls, not the window - the progress strip sits at the top
      // of the page column, so that is what has to come back into view.
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(apiError(err, 'We could not refresh this report'));
    } finally {
      setRestarting(false);
    }
  };

  const download = async () => {
    if (!id) return;
    setDownloading(true);
    try {
      await downloadReportPdf(
        id,
        report?.pdfFileName || `salesmotion-${report?.companyName || 'report'}.pdf`
      );
    } catch (err) {
      setError('Download failed. The PDF may still be rendering — try again in a moment.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <Spinner label="Loading report…" />;

  if (error && !report) {
    return (
      <div className="mx-auto max-w-xl">
        <Alert tone="error">{error}</Alert>
        <div className="mt-4">
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/reports')}>
            Back to reports
          </Button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const facts = report.fastFacts || {};

  // 'Unknown' is the placeholder a company is stored with when no country was
  // resolved at the time it was added. It should read as absent rather than as
  // a place: "Headquartered in Unknown" is worse than no line at all.
  const headquarters = String(facts.headquarters || '')
    .split(',')
    .map((part: string) => part.trim())
    .filter((part: string) => part && part.toLowerCase() !== 'unknown')
    .join(', ');

  // When the pipeline last finished writing this report. Refreshing rewrites
  // the same report, so on one that has been refreshed this is the only date
  // that says how current what is on the page actually is.
  const lastRefreshed = report.lastUpdatedAt || report.generatedAt;

  const context = report.context || {};
  const brief = report.executiveBrief || {};
  const research = report.research || {};
  const value = report.value || {};
  // Records extracted server-side from primary sources. Reports generated
  // before this existed have none, and every block handles that by rendering
  // nothing rather than an empty shell.
  const evidence = report.evidence || {};
  const whitespace = report.whitespace || {};

  // ------------------------------------------------------------------ states
  if (report.status === 'pending') {
    return (
      <div className="mx-auto max-w-2xl">
        <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={() => navigate('/reports')}>
          Back to reports
        </Button>

        <Card className="mt-4 animate-fade-in-up p-10 text-center">
          <span className="relative mx-auto flex h-14 w-14 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-brand-500/15 blur-lg" aria-hidden />
            <Loader2 size={30} className="relative animate-spin text-brand-600" />
          </span>
          <h1 className="mt-5 text-xl font-bold tracking-tight text-ink">
            Writing your {report.companyName} report
          </h1>
          <p className="mx-auto mt-2.5 max-w-md text-sm leading-relaxed text-ink-muted">
            We are reading the latest news, filings and hiring activity, then writing it up around
            what your business sells
            {context.priorityTopics?.length ? `: ${context.priorityTopics.join(', ')}` : ''}. This
            usually takes about a minute.
          </p>

          <div className="mx-auto mt-7 max-w-sm">
            <ProgressBar percent={report.progress?.percent} className="h-2" />
            <p className="mt-2.5 text-[13px] font-medium text-ink-muted">
              {report.progress?.step || 'Getting started'}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (report.status === 'failed') {
    return (
      <div className="mx-auto max-w-2xl">
        <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={() => navigate('/reports')}>
          Back to reports
        </Button>

        <Card className="mt-4 animate-fade-in-up p-10 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 ring-1 ring-inset ring-red-200">
            <AlertTriangle size={26} className="text-red-500" />
          </span>
          <h1 className="mt-5 text-xl font-bold tracking-tight text-ink">
            This report did not finish
          </h1>
          <p className="mx-auto mt-2.5 max-w-md text-sm leading-relaxed text-ink-muted">
            {report.error || 'Something went wrong while we were writing this report.'}
          </p>
          <div className="mt-6">
            <Button
              icon={RefreshCw}
              onClick={async () => {
                try {
                  const res = await reportsAPI.generate(report.companyId);
                  // A report that never finished is re-run in place rather than
                  // replaced, so this is normally the same id - reloading is
                  // what moves the page on, not navigating.
                  if (res.data?.reportId && res.data.reportId !== id) {
                    navigate(`/reports/${res.data.reportId}`);
                    return;
                  }
                  await load(true);
                } catch (err) {
                  setError(apiError(err, 'Could not restart the report'));
                }
              }}
            >
              Try again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ------------------------------------------------------------------ report
  return (
    <div className="pb-10">
      <div className="no-print mb-4 flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={() => navigate('/reports')}>
          All reports
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" icon={Printer} onClick={() => window.print()}>
            Print
          </Button>
          {/* Sits to the left of the export, because it is the thing you do
              instead of exporting when what is here has gone stale. */}
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshCw}
            loading={restarting || refreshing}
            disabled={restarting || refreshing}
            onClick={refreshReport}
          >
            {restarting || refreshing ? 'Refreshing…' : 'Refresh this report'}
          </Button>
          <Button size="sm" icon={Download} loading={downloading} onClick={download}>
            Download PDF
          </Button>
        </div>
      </div>

      {/* The rewrite in progress. Loud on purpose - it has to be obvious that
          what is below is the previous version, not a half-written one. */}
      {(restarting || refreshing) && (
        <div className="no-print shimmer mb-5 animate-slide-down rounded-2xl border border-brand-200 bg-surface shadow-card">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 animate-gradient-pan"
            style={{
              backgroundImage:
                'linear-gradient(110deg, rgb(37 99 235 / 0.12) 0%, rgb(99 102 241 / 0.16) 30%, rgb(37 99 235 / 0.06) 60%, transparent 100%)',
              backgroundSize: '220% 100%',
            }}
          />

          <div className="relative flex items-center gap-4 p-4 sm:p-5">
            <span className="relative flex h-11 w-11 shrink-0 items-center justify-center">
              <span
                aria-hidden
                className="absolute inset-0 animate-pulse-ring rounded-full bg-brand-500/30"
              />
              <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-brand-gradient shadow-brand">
                <Sparkles size={19} className="animate-pulse text-white" />
              </span>
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold tracking-tight text-ink">
                Refreshing the {report.companyName} report
              </p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">
                {report.refresh?.step || 'Getting started'} — everything below stays exactly as it
                is until the new research is ready.
              </p>

              <div className="mt-2.5 flex items-center gap-3">
                <ProgressBar percent={report.refresh?.percent} className="h-1.5 flex-1" />
                {typeof report.refresh?.percent === 'number' && report.refresh.percent > 0 && (
                  <span className="shrink-0 text-2xs font-bold tabular-nums text-brand-600">
                    {Math.min(99, Math.round(report.refresh.percent))}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="no-print mb-4">
          <Alert tone="error" onDismiss={() => setError('')}>
            {error}
          </Alert>
        </div>
      )}

      {/* A refresh that did not land. Said as a warning rather than an error
          page, because nothing was lost — the report below is the one the team
          had before, and it is still the best thing we have on this account. */}
      {report.refresh?.status === 'failed' && !restarting && (
        <div className="no-print mb-4">
          <Alert tone="warning">
            {report.refresh.error ||
              'The last refresh did not finish.'}{' '}
            What you are reading is the previous report, written{' '}
            {lastRefreshed ? day(lastRefreshed) : 'earlier'}.
          </Alert>
        </div>
      )}

      {/* ---------------- Ask anything ----------------
          Sticky above everything else, because the question a rep has arrives
          wherever they happen to be in a document this long - scrolling back to
          the top to ask it is the thing that would stop them asking. */}
      <AskAnything reportId={id!} report={report} />

      {/* ---------------- Cover ---------------- */}
      <Card className="mb-5 animate-fade-in-up overflow-hidden print-block">
        {/* No border-b: with "Written for you" gone this is the only block in
            the cover card */}
        <div
          className="relative p-6 sm:p-8"
          style={{
            backgroundImage:
              'linear-gradient(135deg, rgb(37 99 235 / 0.06) 0%, rgb(99 102 241 / 0.04) 40%, transparent 70%)',
          }}
        >
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                {/* bg-white, not bg-surface: third-party logo art assumes a
                    white backing, and a dark-inked mark would disappear on a
                    dark card. */}
                {facts.logoUrl ? (
                  <img
                    src={facts.logoUrl}
                    alt=""
                    className="h-11 w-11 rounded-lg bg-white object-contain p-1 ring-1 ring-slate-200"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                  />
                ) : (
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50">
                    <Building2 size={20} className="text-brand-600" />
                  </span>
                )}
                <div className="min-w-0">
                  <h1 className="truncate text-[28px] font-extrabold leading-tight tracking-tight text-ink">
                    {report.companyName}
                  </h1>
                  <p className="truncate text-sm text-ink-muted">
                    {facts.website
                      ? facts.website.replace(/^https?:\/\//, '').replace(/\/$/, '')
                      : facts.industry}
                  </p>
                </div>
              </div>
            </div>

            {/* Score. The reasons behind it live in a hover card rather than a
                section of their own - hover (or focus) the badge to see them. */}
            {report.score?.value ? (
              <div
                tabIndex={0}
                className="group relative flex shrink-0 cursor-default flex-col items-center rounded-2xl border border-slate-200 bg-surface px-6 py-5 shadow-card outline-none transition hover:border-brand-300 focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <ScoreRing value={report.score.value} band={report.score.band} size={96} />
                <p className="eyebrow mt-1.5 text-ink-faint">Fit score</p>
                <p className="mt-1 max-w-[9rem] text-center text-2xs leading-snug text-ink-muted">
                  How well they match what you sell
                </p>

                {(report.score.reasons?.length || report.score.summary) && (
                  // pt-2 instead of mt-2 keeps the cursor inside the group
                  // while it crosses from the badge to the card, so the
                  // tooltip does not flicker shut on the way down
                  <div className="no-print invisible absolute right-0 top-full z-30 w-[26rem] max-w-[80vw] pt-2 opacity-0 transition duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="rounded-xl border border-slate-200 bg-surface p-4 text-left shadow-pop">
                      <h3 className="mb-2 text-2xs font-bold uppercase tracking-[0.12em] text-ink-faint">
                        Why we gave this score
                      </h3>
                      {report.score.reasons?.length ? (
                        <ul className="space-y-1.5">
                          {report.score.reasons.map((reason: string, i: number) => (
                            <li
                              key={i}
                              className="flex gap-2 text-[13px] leading-relaxed text-ink-soft"
                            >
                              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-ink-faint" />
                              {reason}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[13px] leading-relaxed text-ink-soft">
                          {report.score.summary}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Fast Facts / Quick Links — the two columns of the cover. The facts
              read as sentences rather than labelled cells: "Headquartered in
              London, United Kingdom" is what a rep would say out loud. */}
          <div className="mt-7 grid gap-x-12 gap-y-7 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h2 className="mb-3 text-lg font-bold tracking-tight text-ink">The basics</h2>

              {facts.description && (
                <div className="max-w-2xl">
                  {/* Clamped by line count rather than cut at a character
                      count: the whole description stays in the DOM, so
                      expanding is instant and printing gets all of it however
                      it happened to be left on screen. */}
                  <p
                    ref={descriptionRef}
                    className={cx(
                      'text-sm leading-relaxed text-ink-soft print:line-clamp-none',
                      !descriptionOpen && 'line-clamp-6'
                    )}
                  >
                    {facts.description}
                  </p>
                  {(descriptionClamped || descriptionOpen) && (
                    <button
                      type="button"
                      onClick={() => setDescriptionOpen((open) => !open)}
                      className="no-print mt-1.5 text-[13px] font-semibold text-brand-600 hover:text-brand-700"
                    >
                      {descriptionOpen ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}

              <ul className="mt-4 space-y-2.5">
                {headquarters && (
                  <FactRow icon={MapPin} value={`Headquartered in ${headquarters}`} />
                )}
                {facts.industry && <FactRow icon={Factory} value={facts.industry} />}
                {money(facts.revenue, facts.revenueCurrency) && (
                  <FactRow
                    icon={Banknote}
                    value={`${money(facts.revenue, facts.revenueCurrency)} revenue${
                      facts.revenueAsOf ? ` (FY${facts.revenueAsOf})` : ''
                    }`}
                  />
                )}
                {money(facts.marketCap) && (
                  <FactRow icon={TrendingUp} value={`${money(facts.marketCap)} market cap`} />
                )}
                {facts.founded ? (
                  <FactRow icon={CalendarDays} value={`Founded ${facts.founded}`} />
                ) : null}
              </ul>

              {report.score?.summary && (
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink-soft">
                  {report.score.summary}
                </p>
              )}
            </div>

            <div>
              <h2 className="mb-3 text-lg font-bold tracking-tight text-ink">Useful links</h2>

              <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                {facts.employees ? (
                  <FactRow
                    icon={Users}
                    value={
                      `${Number(facts.employees).toLocaleString()} employees` +
                      (facts.employeesAsOf ? ` (${facts.employeesAsOf})` : '')
                    }
                  />
                ) : null}
                {quickLinks.map((link: any) => (
                  <FactRow
                    key={link.url}
                    icon={linkIcon(link)}
                    value={linkLabel(link)}
                    href={link.url}
                  />
                ))}
              </ul>

              <h2 className="mb-3 mt-6 text-lg font-bold tracking-tight text-ink">
                About this report
              </h2>
              <ul className="space-y-2.5">
                {report.accountAddedAt && (
                  <FactRow
                    icon={CalendarClock}
                    value={`Company added on ${day(report.accountAddedAt)}`}
                  />
                )}
                {lastRefreshed && (
                  <FactRow
                    icon={RefreshCw}
                    value={`Last updated ${day(lastRefreshed)} at ${clock(lastRefreshed)}`}
                  />
                )}
                <FactRow
                  icon={User}
                  value={`Looked after by ${report.author?.name || 'SalesMotion'}`}
                />
              </ul>
            </div>
          </div>
        </div>

      </Card>

      {/* ---------------- Jump links ---------------- */}
      {/* top-[71px] parks this directly under the ask island, which is sticky at
          0 and 71px tall (10px padding + 1.5px rim + a 48px bar, doubled on the
          vertical). Both bars stay visible instead of stacking on top of each
          other. */}
      <div className="no-print glass sticky top-[71px] z-20 -mx-6 mb-5 border-b border-slate-200/80 px-6">
        <nav className="flex gap-1 overflow-x-auto scrollbar-none">
          {CHAPTERS.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              className={cx(
                'relative whitespace-nowrap px-3.5 py-3.5 text-sm font-semibold transition-colors duration-200',
                activeChapter === c.id ? 'text-brand-700' : 'text-ink-muted hover:text-ink'
              )}
            >
              {c.label}
              {/* Underline slides in rather than snapping between links */}
              <span
                aria-hidden
                className={cx(
                  'absolute inset-x-2 bottom-0 h-0.5 origin-left rounded-full bg-brand-gradient transition-transform duration-300 ease-swift',
                  activeChapter === c.id ? 'scale-x-100' : 'scale-x-0'
                )}
              />
            </a>
          ))}
        </nav>
      </div>

      {/* ---------------- Report body ---------------- */}
      <div className="space-y-5">
        <Chapter {...CHAPTERS[0]} />

        {/* Before anything it qualifies. A reader who has absorbed six insights
            before being told the report ran on nine sources has been misled by
            the ordering alone. */}
        <CoverageWarning coverage={report.coverage} />

        <ReportSection
          id="key-insights"
          title="Key insights"
          description="The things that matter most for your pitch."
        >
          {/* Verified records first, in trigger order. Programmes and
              regulatory actions share the top tier: both are dated commitments
              the account has made, and both are openings a rep can use. */}
          <ProgramBlock programs={evidence.strategicPrograms} sources={sources} />
          <RegulatoryBlock actions={evidence.regulatoryActions} sources={sources} />
          <ContractBlock awards={evidence.contractAwards} sources={sources} compact />
          <ResultsBlock results={evidence.latestResults} sources={sources} />
          <HiringBlock hiring={evidence.hiring} sources={sources} />

          <InsightList items={brief.keyInsights} sources={sources} tone="amber" />
        </ReportSection>

        <div className="grid gap-5 lg:grid-cols-2">
          <ReportSection id="opportunities" title="Opportunities">
            <InsightList items={brief.opportunities} sources={sources} tone="green" />
          </ReportSection>

          <ReportSection id="challenges" title="Challenges">
            <InsightList items={brief.challenges} sources={sources} tone="red" />
          </ReportSection>
        </div>

        {/* Verified moves, newest first, then what the model reads into them.
            This section never says "no specific executives mentioned" - it
            either lists the people or states plainly there were none. */}
        <ReportSection id="people" title="Who is moving">
          <PeopleBlock moves={evidence.executiveMoves} sources={sources} />
          {brief.peopleUpdates?.length ? (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <InsightList items={brief.peopleUpdates} sources={sources} tone="purple" />
            </div>
          ) : null}
        </ReportSection>

        <ReportSection id="news" title="Recent news">
          <NewsList items={brief.topNews} sources={sources} />
        </ReportSection>

        <ReportSection
          id="talking-points"
          title="What to say on the call"
          description="Say these out loud — each one gives you the fact, the question to ask, the proof to share and the pushback to expect."
        >
          <TalkingPointList items={brief.talkingPoints} sources={sources} />
        </ReportSection>

        {brief.executivePerspective?.length ? (
          <ReportSection id="quotes" title="In their own words">
            <div className="space-y-3">
              {brief.executivePerspective.map((q: any, i: number) => (
                <QuoteCard key={i} {...q} />
              ))}
            </div>
          </ReportSection>
        ) : null}

        <Chapter {...CHAPTERS[1]} />

        <ReportSection id="insights" title="Company research">
          <SubSection title="Company overview">
            <InsightList items={research.companyOverview} sources={sources} />
          </SubSection>
          <SubSection title="People changes" tone="purple">
            <InsightList items={research.keyPeopleChanges} sources={sources} tone="purple" />
          </SubSection>
          <SubSection title="Projects underway" tone="teal">
            <InsightList items={research.keyProjects} sources={sources} tone="teal" />
          </SubSection>
          <SubSection title="What they want to become">
            <InsightList items={research.aspirations} sources={sources} />
          </SubSection>
          <SubSection title="Their goals">
            <InsightList items={research.businessGoals} sources={sources} />
          </SubSection>
          <SubSection title="Opportunities" tone="green">
            <InsightList items={research.opportunities} sources={sources} tone="green" />
          </SubSection>
          <SubSection title="The bigger picture" tone="slate">
            <InsightList items={research.macroPerspective} sources={sources} tone="slate" />
          </SubSection>
          <SubSection title="Recent announcements" tone="teal">
            <InsightList items={research.recentPress} sources={sources} tone="teal" />
          </SubSection>
        </ReportSection>

        <ReportSection id="business-model" title="How they make money">
          <SubSection title="Where the money comes from">
            <InsightList items={research.businessModel?.revenueStreams} sources={sources} />
          </SubSection>
          <SubSection title="How they sell">
            <InsightList items={research.businessModel?.goToMarket} sources={sources} />
          </SubSection>
          <SubSection title="Who they sell to">
            <InsightList
              items={research.businessModel?.idealCustomerProfile}
              sources={sources}
            />
          </SubSection>
        </ReportSection>

        <div className="grid gap-5 lg:grid-cols-2">
          <ReportSection id="initiatives" title="What they are investing in">
            <InsightList items={research.strategicInitiatives} sources={sources} />
          </ReportSection>
          <ReportSection id="financials" title="The numbers">
            <InsightList items={research.financials} sources={sources} tone="teal" />
          </ReportSection>
        </div>

        <ReportSection
          id="swot"
          title="Strengths, weaknesses, opportunities and threats"
          description="Read from your angle: can this company buy, and what stands in the way?"
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <SubSection title="Strengths" tone="green">
              <InsightList items={research.swot?.strengths} sources={sources} tone="green" />
            </SubSection>
            <SubSection title="Weaknesses" tone="red">
              <InsightList items={research.swot?.weaknesses} sources={sources} tone="red" />
            </SubSection>
            <SubSection title="Opportunities" tone="brand">
              <InsightList items={research.swot?.opportunities} sources={sources} tone="brand" />
            </SubSection>
            <SubSection title="Threats" tone="amber">
              <InsightList items={research.swot?.threats} sources={sources} tone="amber" />
            </SubSection>
          </div>
        </ReportSection>

        {/* Patents and federal awards are dated public record, filed months
            before the programme they belong to is announced. That makes them
            the wrong thing to open a call with and the right thing to shape a
            roadmap around - hence direction here, triggers in Key insights. */}
        <ReportSection
          id="whitespace"
          title="Whitespace"
          description="R&D and public-sector activity on the public record — where this account is investing before it has announced anything."
        >
          <PatentBlock patents={evidence.patents} sources={sources} />
          <ContractBlock awards={evidence.contractAwards} sources={sources} />

          {whitespace.insights?.length ? (
            <SubSection title="What this points to" tone="teal">
              <InsightList items={whitespace.insights} sources={sources} tone="teal" />
            </SubSection>
          ) : null}

          {whitespace.capabilityGaps?.length ? (
            <SubSection title="Capability gaps it opens" tone="green">
              <InsightList items={whitespace.capabilityGaps} sources={sources} tone="green" />
            </SubSection>
          ) : null}

          {!evidence.patents?.length &&
          !evidence.contractAwards?.length &&
          !whitespace.insights?.length &&
          !whitespace.capabilityGaps?.length ? (
            <p className="text-[13px] italic text-ink-faint">
              No patent filings or US federal contract awards are on record for this account.
              These crawls run only for accounts tagged as R&amp;D-heavy or government-facing.
            </p>
          ) : null}
        </ReportSection>

        <Chapter {...CHAPTERS[2]} />

        <ReportSection
          id="three-whys"
          title="Why change, why now, why you"
          description="The argument to make in the room."
        >
          <SubSection title="Why they should change" tone="purple">
            <InsightList items={value.whyChange} sources={sources} tone="purple" />
          </SubSection>
          <SubSection title="Why they should act now" tone="amber">
            <InsightList items={value.whyNow} sources={sources} tone="amber" />
          </SubSection>
          <SubSection title="Why they should choose you" tone="green">
            <InsightList items={value.whyYou} sources={sources} tone="green" />
          </SubSection>
        </ReportSection>

        <ReportSection
          id="value-pyramid"
          title="From their goals to your value"
          description="Follow the thread from what they are trying to achieve down to where you fit."
        >
          <SubSection title="What they are trying to achieve">
            <InsightList items={value.valuePyramid?.companyGoals} sources={sources} />
          </SubSection>
          <SubSection title="How they plan to get there">
            <InsightList items={value.valuePyramid?.businessStrategy} sources={sources} />
          </SubSection>
          <SubSection title="What is getting in the way" tone="red">
            <InsightList
              items={value.valuePyramid?.challengesObstacles}
              sources={sources}
              tone="red"
            />
          </SubSection>
          <SubSection title="Where you can help" tone="teal">
            <InsightList items={value.valuePyramid?.valuePaths} sources={sources} tone="teal" />
          </SubSection>
        </ReportSection>

        {value.valuePropositions?.length ? (
          <ReportSection
            id="value-props"
            title="Ready-made pitch lines"
            description="Copy these straight into a deck or an email."
          >
            <div className="space-y-4">
              {value.valuePropositions.map((prop: any, i: number) => (
                <div
                  key={i}
                  className="print-block rounded-xl border border-slate-200 bg-surface-2 p-4 transition hover:border-brand-200 hover:shadow-card"
                >
                  <h4 className="mb-1.5 flex items-start gap-2 text-sm font-bold text-ink">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-brand-50 text-2xs text-brand-700 ring-1 ring-inset ring-brand-200">
                      {i + 1}
                    </span>
                    {prop.title}
                  </h4>
                  <p className="report-body">{prop.body}</p>
                </div>
              ))}
            </div>
          </ReportSection>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-2">
          <ReportSection
            id="hypotheses"
            title="Ideas to test"
            description="Hunches worth checking on the call."
          >
            <InsightList items={value.hypotheses} sources={sources} tone="purple" />
          </ReportSection>

          <ReportSection id="pov" title="Our take">
            <InsightList items={value.pointOfView} sources={sources} tone="brand" />
          </ReportSection>
        </div>

        <Chapter {...CHAPTERS[3]} />

        <ReportSection
          id="sources"
          title="Where this came from"
          description={`${sources.length} sources we read while writing this report.`}
        >
          <SourcesList sources={sources} />
        </ReportSection>
      </div>

      <footer className="no-print mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5 text-[13px] text-ink-muted">
        <span>
          Written {new Date(report.generatedAt).toLocaleString()}
          {report.aiModel ? ` · ${report.aiModel}` : ''}
        </span>
        <Link to="/accounts" className="font-semibold text-brand-600 hover:underline">
          Back to your companies
        </Link>
      </footer>
    </div>
  );
}

// Divider between the four parts of the document. Gives the long scroll an
// obvious structure, and gives the jump links something to anchor to.
function Chapter({ id, label, blurb }: { id: string; label: string; blurb?: string }) {
  return (
    <div id={id} className="scroll-mt-36 pt-6 first:pt-0 print-block">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b-2 border-slate-200 pb-2.5">
        <h2 className="text-xl font-extrabold tracking-tighter text-ink">{label}</h2>
        {blurb && <p className="text-[13px] text-ink-muted">{blurb}</p>}
      </div>
    </div>
  );
}

/** One line of the cover: a mark, then the fact itself or the link it points at. */
function FactRow({
  icon: Icon,
  value,
  href,
}: {
  icon: React.ElementType;
  value: string;
  href?: string;
}) {
  return (
    <li className="flex items-start gap-2.5 text-[13.5px] leading-snug">
      <Icon size={15} className="mt-0.5 shrink-0 text-brand-600" />
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 break-words font-medium text-brand-600 underline-offset-2 hover:underline"
        >
          {value}
        </a>
      ) : (
        <span className="min-w-0 break-words text-ink-soft">{value}</span>
      )}
    </li>
  );
}

